<template>
  <v-container class="pa-0">
    <v-col
      class="card-location"
      :class="{ selected: selected, dashed: dashed, disabled: disabled }"
    >
      <!-- 1st Row -->
      <v-row class="ma-0">
        <v-col class="pa-0" cols="8">
          <v-row class="ma-0 no-text-wrap token-text-mediumbold">
            <Icon icon="location" :color="colorIcon" class="mr-2 ml-0" />
            Hosting area
          </v-row>
          <v-row class="ma-0 no-text-wrap token-text-regular">
            <Flag :code="location.attributes.country" class="mr-2 ml-0" />
            {{ $helpers.providers.locationToDisplayName(location) }}
          </v-row>
        </v-col>
        <v-col class="pa-0">
          <v-row class="ma-0 no-text-wrap token-text-mediumbold">
            <Icon icon="gpu" :color="colorIcon" class="mr-2 ml-0" />
            Has GPUs
          </v-row>
          <v-row class="ma-0 no-text-wrap token-text-regular">
            <Indicator
              :state="hasGPU ? 'success' : 'neutral'"
              class="mr-2 ml-0"
            />
            {{ hasGPU ? "Yes" : "No" }}
          </v-row>
        </v-col>
      </v-row>
      <!-- / 1st Row -->

      <!-- 2nd Row -->
      <v-row class="ma-0">
        <v-col class="pa-0" cols="8">
          <v-row class="ma-0 no-text-wrap token-text-mediumbold">
            <Icon icon="publish" :color="colorIcon" class="mr-2 ml-0" />
            Provider
          </v-row>
          <v-row class="ma-0 no-text-wrap token-text-regular fit-content">
            <Logo class="mr-2" :logo="location.attributes.provider" />
            <span>{{ providerLabel }}</span>
          </v-row>
        </v-col>
        <v-col class="pa-0">
          <v-row class="ma-0 no-text-wrap token-text-mediumbold">
            <Icon icon="credit-card" :color="colorIcon" class="mr-2 ml-0" />
            Price
          </v-row>
          <v-row class="ma-0 no-text-wrap token-text-regular">
            <template
              v-if="minPrice.includes('Infinity') || minPrice.includes('NaN')"
              >-</template
            >
            <template v-else-if="minPrice">From {{ minPrice }}</template>
            <template v-else><Spinner /></template>
          </v-row>
        </v-col>
      </v-row>
      <!-- / 2nd Row -->
    </v-col>
  </v-container>
</template>

<script lang="ts">
import {
  APIClusterLocation,
  APIClusterNodeTypologie,
  APIInfrastructureProvider,
} from "@/typesAPI";
import { Options, Vue, prop } from "vue-class-component";

import Icon from "@/components/UIElements/Icon.vue";
import Logo from "@/components/UIElements/Logo.vue";
import Spinner from "@/components/UIElements/Spinner.vue";
import Flag from "@/components/UIElements/Flag.vue";
import Indicator from "@/components/UIElements/Indicator.vue";

class Props {
  location: APIClusterLocation = prop({
    required: true,
  });
  selected?: boolean = prop({
    required: false,
  });
  disabled?: boolean = prop({
    required: false,
  });
  dashed?: boolean = prop({
    required: false,
  });
  minPrice?: string = prop({
    required: false,
  });
}

@Options({
  components: {
    Icon,
    Logo,
    Spinner,
    Flag,
    Indicator,
  },
})
export default class CardLocation extends Vue.with(Props) {
  colorIcon = "rgba(0, 0, 58, 0.3)";

  get providers(): APIInfrastructureProvider[] {
    return this.$store.getters["clusters/getProviders"];
  }

  get providerLabel(): string {
    const provider = this.providers.find(
      (provider) => provider.id === this.location.attributes.provider,
    );
    return provider?.attributes?.name ?? "";
  }

  get hasGPU(): boolean {
    if (!this.location.attributes.available_server_typologies.length) {
      return false;
    }
    const typos: APIClusterNodeTypologie[] = this.$store.getters[
      "clusters/getClusterNodeTypologiesByProviderAndLocation"
    ](this.location.attributes.provider, this.location.id);
    return !!typos.find((typo) => typo.attributes.gpus.length > 0);
  }
}
</script>

<style lang="scss">
@import "@/css/variables";
.card-location {
  border: 1px solid $color-neutral-grey-24;
  &.disabled {
    background: $color-neutral-grey-20;
    cursor: not-allowed;
  }
  &.dashed {
    border-style: dashed;
  }
  border-radius: 8px;
  padding: 16px;
  &.selected {
    box-shadow: 0px 0px 0px 2px $color-brand;
  }
}
</style>
